import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Spin } from 'antd';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';

import { authActions } from 'redux/actions';
import { device } from 'utils';

const FormItem = Form.Item;

const ForgetPasswordWrapper = styled.div`
  width: 375px;
  max-width: 400px;
  background: #ffffff;
  box-shadow: 0px 20px 24px -4px rgba(10, 13, 18, 0.08),
    0px 8px 8px -4px rgba(10, 13, 18, 0.03),
    0px 3px 3px -1.5px rgba(10, 13, 18, 0.04);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media ${device.allMobile} {
    width: 100%;
    min-width: 1px;
    max-width: 100%;
    min-height: auto;
    box-shadow: none;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TitleGroup = styled.div`
  width: 100%;
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #181d27;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const Spacer = styled.div`
  height: 20px;
`;

const Body = styled.div`
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label {
    padding-bottom: 6px;

    > label {
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #414651;
      height: auto;
    }
  }

  .ant-input {
    height: 44px;
    padding: 10px 14px;
    border: 1px solid #d5d7da;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #181d27;
  }

  .ant-input::placeholder {
    color: #717680;
  }

  .ant-input:focus,
  .ant-input-focused {
    border-color: #9dbc60;
    box-shadow: 0 0 0 2px rgba(157, 188, 96, 0.12);
  }

  .ant-form-item-explain,
  .ant-form-item-explain-error {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #d92d20;
    margin-top: 6px;
  }

  input[type='email'] {
    @media ${device.allMobile} {
      font-size: 16px !important;
      -webkit-text-size-adjust: 100%;
      -webkit-tap-highlight-color: transparent;
    }
  }
`;

const LinkRow = styled.div`
  margin-top: 4px;
  margin-bottom: 12px;

  a {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #3f621a;
    text-decoration: none;
  }
`;

const Actions = styled.div`
  padding: 32px 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 8px;
  background: #9dbc60;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
`;

class ForgetPassword extends Component {
  static propTypes = {
    handleCurrentFormState: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    errors: PropTypes.array,
    resetPassword: PropTypes.func.isRequired,
  };

  onFinish = (values) => {
    const { resetPassword } = this.props;
    resetPassword(values.email);
  };

  onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  render() {
    const { handleCurrentFormState, loading } = this.props;

    return (
      <Spin spinning={loading}>
        <ForgetPasswordWrapper>
          <Header>
            <TitleGroup>
              <Title>Passwort zurücksetzen</Title>
              <Subtitle>
                Bitte gib deine E-Mail-Adresse an,
                <br />
                um dein Passwort zurückzusetzen.
              </Subtitle>
            </TitleGroup>
          </Header>

          <Spacer />

          <Body>
            <StyledForm
              name="reset-password"
              layout="vertical"
              onFinish={this.onFinish}
              onFinishFailed={this.onFinishFailed}
            >
              <FormItem
                name="email"
                label="E-Mail"
                rules={[
                  {
                    required: true,
                    message: 'Bitte gib deine E-Mail-Adresse ein.',
                  },
                ]}
              >
                <Input type="email" placeholder="Deine E-Mail-Adresse" />
              </FormItem>

              <LinkRow>
                <Link to="#!" onClick={() => handleCurrentFormState('login')}>
                  Bereits registriert? Jetzt anmelden
                </Link>
              </LinkRow>

              <Actions>
                <SubmitButton type="submit">Passwort zurücksetzen</SubmitButton>
              </Actions>
            </StyledForm>
          </Body>
        </ForgetPasswordWrapper>
      </Spin>
    );
  }
}

const mapStateToProps = (state) => {
  const { user, loading, errors } = state.authentication;
  return {
    user,
    loading,
    errors,
  };
};

const mapDispatchToProps = (dispatch) => ({
  resetPassword: (user) => dispatch(authActions.resetPassword(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ForgetPassword);
